"use client";

import React from "react";
import { motion } from "framer-motion";
import { IoShieldCheckmark } from "react-icons/io5";
import {
  staggerContainer,
  staggerItem,
  fadeInUp,
  textReveal,
} from "../lib/animations";

interface ModalLink {
  text: string;
  url: string;
}

interface ModalBullet {
  text: string;
  links?: ModalLink[];
}

interface ModalParagraph {
  text?: string;
  isBold?: boolean;
  bullets?: ModalBullet[];
  links?: ModalLink[];
  buttons?: ModalButton[];
}

interface ModalButton {
  text: string;
  action?: () => void;
  variant?: "primary" | "secondary";
}

interface ModalSection {
  paragraphs: ModalParagraph[];
  images?: string[];
  layout?: "text-only" | "text-image" | "image-text" | "text-top" | "image-top";
  title?: string;
}

const renderLinks = (links: ModalLink[]) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {links.map((link, idx) => (
      <a
        key={idx}
        href={link.url}
        className="text-blue-600 hover:text-blue-800 underline lg:text-xl text-md"
        target="_blank"
        rel="noopener noreferrer"
      >
        {link.text}
      </a>
    ))}
  </div>
);

const renderBullets = (bullets: ModalBullet[]) => (
  <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
    {bullets.map((bullet, idx) => (
      <li key={idx} className="text-gray-600 lg:text-xl text-md">
        {bullet.text}
        {bullet.links && renderLinks(bullet.links)}
      </li>
    ))}
  </ul>
);

const renderParagraph = (paragraph: ModalParagraph, idx: number) => (
  <motion.div
    key={idx}
    className="mb-4"
    initial={{ opacity: 0, y: 15 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: idx * 0.1 }}
  >
    <p
      className={`text-gray-600 lg:text-xl text-xl leading-relaxed ${
        paragraph.isBold ? "font-semibold" : ""
      }`}
    >
      {paragraph.text}
      {paragraph.links && renderLinks(paragraph.links)}
    </p>
    {paragraph.bullets && renderBullets(paragraph.bullets)}
  </motion.div>
);

const renderSection = (section: ModalSection, sectionIdx: number) => (
  <div key={sectionIdx} className="mb-8">
    <div
      className={`${
        section.layout === "text-image"
          ? "grid grid-cols-1 lg:grid-cols-1 lg:gap-6 gap-2"
          : section.layout === "image-text"
          ? "grid grid-cols-1 lg:grid-cols-2 lg:gap-6 gap-2"
          : section.layout === "text-top"
          ? "flex flex-col lg:gap-6 gap-2"
          : section.layout === "image-top"
          ? "flex flex-col lg:gap-6 gap-2"
          : "block"
      }`}
    >
      {/* Text Content */}
      <div
        className={`w-full bg-[#f5f5f7] rounded-3xl ${
          section?.layout === "text-only" || !section?.layout
            ? " p-5"
            : "pb-0 lg:pt-10 pt-5 p-10"
        }`}
      >
        {section?.title && (
          <motion.h3
            className="lg:text-3xl text-lg font-bold text-gray-800 mb-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {section?.title}
          </motion.h3>
        )}
        <div
          className={`${
            section?.images && section?.images?.length === 0 ? "pb-0" : ""
          } ${
            section.layout === "image-text"
              ? "order-2"
              : section.layout === "image-top"
              ? "order-2"
              : "lg:mb-10 mb-3 "
          }`}
        >
          {section.paragraphs.map((paragraph, idx) =>
            renderParagraph(paragraph, idx)
          )}
        </div>
      </div>
    </div>
  </div>
);

const privacyData: {
  category: string;
  title: string;
  subtitle: string;
  sections: ModalSection[];
} = {
  category: "Legal & Compliance",
  title: "Privacy Policy",
  subtitle: "How we collect, use, and protect your personal information",
  sections: [
    {
      title: "Information We Collect",
      paragraphs: [
        {
          text: "We may collect personal information such as your name, email address, phone number, and company details when you:",
          bullets: [
            { text: "Contact us for services or support" },
            { text: "Subscribe to our newsletter" },
            { text: "Request a quote or consultation" },
            { text: "Use our website or services" },
          ],
        },
        {
          text: "We automatically collect certain technical information including:",
          bullets: [
            { text: "IP address and browser type" },
            { text: "Pages visited and time spent on our website" },
            { text: "Device information and operating system" },
            { text: "Cookies and similar tracking technologies" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "How We Use Your Information",
      paragraphs: [
        {
          text: "We use the information we collect to:",
          bullets: [
            { text: "Provide and improve our services" },
            { text: "Communicate with you about our services" },
            { text: "Send you marketing materials (with your consent)" },
            { text: "Analyze website usage and improve user experience" },
            { text: "Comply with legal obligations" },
            { text: "Protect against fraud and security threats" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "Information Sharing and Disclosure",
      paragraphs: [
        {
          text: "We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:",
          bullets: [
            { text: "With your explicit consent" },
            { text: "To comply with legal requirements or court orders" },
            { text: "To protect our rights, property, or safety" },
            {
              text: "With trusted service providers who assist in our operations",
            },
            { text: "In connection with a business transfer or merger" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "Data Security",
      paragraphs: [
        {
          text: "We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:",
          bullets: [
            { text: "Encryption of data in transit and at rest" },
            { text: "Regular security assessments and updates" },
            { text: "Access controls and authentication measures" },
            { text: "Employee training on data protection" },
            { text: "Incident response procedures" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "Your Rights and Choices",
      paragraphs: [
        {
          text: "You have the following rights regarding your personal information:",
          bullets: [
            { text: "Access and review your personal data" },
            { text: "Request correction of inaccurate information" },
            { text: "Request deletion of your personal data" },
            { text: "Object to processing of your data" },
            { text: "Withdraw consent for marketing communications" },
            { text: "Request data portability" },
          ],
        },
        {
          text: "To exercise these rights, please contact us at hello@zysoftec.com",
        },
      ],
      layout: "text-only",
    },
    {
      title: "Cookies and Tracking Technologies",
      paragraphs: [
        {
          text: "We use cookies and similar technologies to enhance your browsing experience, analyze website traffic, and understand where our visitors are coming from. You can control cookie settings through your browser preferences.",
        },
        {
          text: "Types of cookies we use:",
          bullets: [
            { text: "Essential cookies for website functionality" },
            { text: "Analytics cookies to understand usage patterns" },
            { text: "Marketing cookies for personalized content" },
            { text: "Preference cookies to remember your settings" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "Third-Party Services",
      paragraphs: [
        {
          text: "Our website may contain links to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "Children's Privacy",
      paragraphs: [
        {
          text: "Our services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "International Data Transfers",
      paragraphs: [
        {
          text: "Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "Changes to This Privacy Policy",
      paragraphs: [
        {
          text: "We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated policy on our website and updating the 'Last Updated' date.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "Contact Us",
      paragraphs: [
        {
          text: "If you have any questions about this Privacy Policy or our data practices, please contact us:",
        },
        {
          text: "Email: hello@zysoftec.com",
          isBold: true,
        },
        {
          text: "Phone: +92 3175682252",
          isBold: true,
        },
        {
          text: "Address: ZySoftec, Pakistan",
          isBold: true,
        },
      ],
      layout: "text-only",
    },
  ],
};

export default function PrivacyPolicy() {
  return (
    <motion.div
      className="min-h-screen bg-white"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Header */}
      <motion.div
        className="lg:pt-20 pt-5 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {privacyData.category && (
          <motion.p
            className="lg:text-base text-sm text-gray-500 font-medium mb-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {privacyData.category}
          </motion.p>
        )}
        <motion.h2
          className="lg:text-4xl text-2xl font-bold text-gray-900 leading-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {privacyData.title}
        </motion.h2>

        {privacyData.subtitle && (
          <motion.p
            className="lg:text-lg text-sm text-gray-600 mt-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            {privacyData.subtitle}
          </motion.p>
        )}
      </motion.div>

      {/* Main Content */}
      <div className="lg:pt-10 pt-5 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-5">
        {privacyData.sections.map((section, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 + idx * 0.1 }}
          >
            {renderSection(section, idx)}
          </motion.div>
        ))}
      </div>

      {/* Last Updated */}
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 1 }}
      >
        <div className="bg-gray-50 rounded-lg p-4 text-center max-w-4xl mx-auto">
          <p className="text-sm text-gray-600">
            <strong>Last Updated:</strong> December 2024
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
